// Feb 2026 v31 — Customer PFA page: mostra maturazione + erogabilità.
// Regola definitiva: la maturazione è SEMPRE reale. Il payout può essere gated
// dal requisito "nuovo inserimento SKU". Non usare mai frasi tipo
// "il PFA inizierà a maturare quando…" — la maturazione non si blocca.
import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { PfaTiersTable } from "../components/PfaTiers";
import { PfaScopeSummary } from "../components/PfaScopeSummary";
import { Section, Card, Alert, Badge } from "../design-system";

export default function CustomerPfaPage() {
  const { apiGet } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try { setRows(await apiGet("/api/customer/pfa/summary")); }
      catch (e) { setErr(e?.message || String(e)); setRows([]); }
    })();
  }, []);
  return (
    <Section title="I miei PFA"
             subtitle="Piano Fatturato Annuale — maturazione e stato erogazione"
             data-testid="customer-pfa-page">
      {err && <Alert variant="danger">{err}</Alert>}
      {!rows && <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>}
      {rows && rows.length === 0 && (
        <Alert tone="info" data-testid="customer-pfa-empty">
          Nessun PFA attivo. I PFA vengono configurati nel Contratto commerciale
          e diventano visibili qui non appena inizia il periodo di validità.
        </Alert>
      )}
      {rows && rows.map((p) => {
        const gateActive = p.requires_new_product_for_payout &&
                           p.aggregation_mode !== "GROUP_AGGREGATED";
        return (
          <Card key={p.pfa_id} data-testid={`customer-pfa-card-${p.pfa_id}`}
                style={{ marginBottom: 12 }}>
            <Card.Header>
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <strong data-testid={`customer-pfa-name-${p.pfa_id}`}
                          style={{ fontSize: "1.05rem", wordBreak: "break-word" }}>
                    {p.display_name || p.name || `PFA ${p.period_start} → ${p.period_end}`}
                  </strong>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {p.period_start} → {p.period_end}
                  </div>
                  <PfaScopeSummary pfa={p} testidPrefix={`customer-pfa-${p.pfa_id}`} />
                </div>
                {p.payout_eligible
                  ? <Badge tone="success" data-testid={`pfa-payout-eligible-${p.pfa_id}`}>Premio: Erogabile</Badge>
                  : <Badge tone="warning" data-testid={`pfa-payout-not-eligible-${p.pfa_id}`}>Premio: Non ancora erogabile</Badge>}
              </div>
            </Card.Header>
            <Card.Body>
              {gateActive && p.new_product_requirement_met === false && (
                <Alert tone="warning" data-testid={`pfa-new-product-required-${p.pfa_id}`}
                       style={{ marginBottom: 10 }}>
                  <strong>Il tuo PFA sta maturando regolarmente</strong>, ma il premio
                  potrà essere erogato solo dopo l'inserimento di almeno un nuovo
                  prodotto.
                  <div style={{ fontSize: 12, marginTop: 4, color: "#78350F" }}>
                    Nuovo prodotto = SKU mai acquistato prima.
                  </div>
                </Alert>
              )}
              {gateActive && p.new_product_requirement_met === true && (
                <Alert tone="success" data-testid={`pfa-new-product-met-${p.pfa_id}`}
                       style={{ marginBottom: 10 }}>
                  Requisito nuovo prodotto: <strong>Soddisfatto</strong>. Il premio
                  maturato è ora erogabile secondo le condizioni contrattuali.
                </Alert>
              )}
              <div style={{ display: "grid",
                            gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))",
                            gap: 10 }}>
                <MetricBox label="Progressivo (kg)" value={p.matured_kg}
                           testid={`pfa-matured-${p.pfa_id}`} />
                {(() => {
                  // Mar 2026 UX — mostra come obiettivo il PROSSIMO SCAGLIONE
                  // se presente; altrimenti il target commerciale finale.
                  const matured = Number(p.matured_kg) || 0;
                  const target = Number(p.target_kg) || 0;
                  const nextMissing = p.next_tier_missing_kg != null
                    ? Number(p.next_tier_missing_kg) : null;
                  const hasNextTier = nextMissing != null && nextMissing > 0;
                  const goal = hasNextTier ? matured + nextMissing : target;
                  const label = hasNextTier ? "Prossimo scaglione (kg)" : "Target (kg)";
                  return (
                    <MetricBox label={label} value={goal}
                               testid={`pfa-goal-${p.pfa_id}`} />
                  );
                })()}
                <MetricBox label="Scaglioni raggiunti" value={`${p.tiers_reached ?? 0}/${p.tiers_total ?? 0}`}
                           testid={`pfa-tiers-${p.pfa_id}`} />
                <MetricBox label="Reward maturato"
                           value={p.cumulative_reward_percent != null
                             ? `${p.cumulative_reward_percent}%` : "—"} />
                {gateActive && (
                  <MetricBox label="Requisito nuovo prodotto"
                             value={p.new_product_requirement_met
                               ? "Soddisfatto" : "Da soddisfare"}
                             tone={p.new_product_requirement_met ? "success" : "warning"} />
                )}
              </div>
              {(p.next_tier_missing_kg != null && Number(p.next_tier_missing_kg) > 0) && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#475569" }}
                     data-testid={`pfa-next-tier-hint-${p.pfa_id}`}>
                  {Number(p.next_tier_missing_kg).toLocaleString("it-IT")} kg
                  al prossimo scaglione · Obiettivo finale{" "}
                  <strong>{Number(p.target_kg).toLocaleString("it-IT")} kg</strong>
                </div>
              )}
              {p.tiers && p.tiers.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                    Scaglioni PFA
                  </div>
                  <PfaTiersTable tiers={p.tiers}
                                 maturedKg={Number(p.matured_kg) || 0}
                                 testidPrefix={`pfa-tier-${p.pfa_id}`} />
                </div>
              )}
            </Card.Body>
          </Card>
        );
      })}
    </Section>
  );
}

function MetricBox({ label, value, testid, tone }) {
  const bg = tone === "success" ? "#DCFCE7" : tone === "warning" ? "#FEF3C7" : "#F8FAFC";
  const border = tone === "success" ? "#86EFAC" : tone === "warning" ? "#FCD34D" : "#E2E8F0";
  return (
    <div data-testid={testid}
         style={{ padding: 8, background: bg, border: `1px solid ${border}`,
                  borderRadius: 6, textAlign: "center" }}>
      <div style={{ fontSize: 11, color: "#64748B" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}
